<script setup lang="ts">

import { ref, watch } from "vue";
import { type ICondition, ConditionType, ConditionState } from "@domain/Vehicle";
import { useNotify } from "@library/Components";
import type { IMyGarageViewModel } from "@app/MyGarage";
import { useConditionApi } from "@app/MyGarage";

const model = defineModel<ICondition>( { required: true } );
const { viewModel } = defineProps<{ viewModel: IMyGarageViewModel }>();
const dummyEnabled = ref( true );

const conditionIcon = ( condition: ICondition ) =>
{
    if( condition.hasAlert ) return 'flag';
    if( condition.isUnconfigured ) return 'settings';
    return 'check';
};

/**
 * Occurs when the user attempts to disable a mandatory group
 * @param condition 
 */
async function mandatoryDisabledClicked( condition: ICondition )
{
    dummyEnabled.value = true;
    useNotify( `Sorry, ${ condition.labels.name } can't be disabled` );
}

/**
 * Save the enabled/disabled state of the users condition if they toggle it
 */
watch( () => model.value.isEnabled, async isEnabled => 
{
    const api = useConditionApi();
    if( isEnabled )
    {
        await api.enable( model.value.conditionId ) 
        return;
    }
    await api.disable( model.value.conditionId );
});

</script>

<template>

    <div class="grid grid-cols-12 py-2 items-center text-base 3xl:text-lg">

        <div class="col-span-8">
            <div class="uppercase flex items-center gap-x-2">
                {{ model.labels.name }}
            </div>
            <div class="opacity-60"
                :class="{
                    'text-amber-500 dark:text-amber-400 !opacity-100': model.isDue,
                    '!text-rose-500 !opacity-100': model.isCritical || model.isOverdue || model.score <= 0.1,
                }">
                <template v-if="model.isCritical">
                    <span v-if="model.type == ConditionType.Recall">
                        {{ model.isCritical ? 'Recall issued' : 'None' }}
                    </span>
                    <span v-else>
                        Issue detected
                    </span>
                </template>
                <span v-else-if="model.isDisabled">
                    Disabled
                </span>
                <span v-else-if="model.isOverdue">
                    Overdue
                </span>
                <span v-else-if="model.isDue">
                    Due
                </span>
                <span v-else-if="model.isUnconfigured">
                    Not set
                </span>
                <span v-else>
                    OK / Healthy
                </span>
            </div>
        </div>
        <div class="col-span-2 flex items-center justify-end">
            <IconButton :name="conditionIcon( model )" 
                        class="text-2xl drop-shadow-lg"
                        :class="{
                            'opacity-10': model.isUnconfigured,
                            'text-secondary-500': model.hasAlert == false && model.state != ConditionState.Incomplete,
                            'text-amber-500 dark:text-amber-400': model.isDue,
                            '!text-rose-500': model.isCritical || model.isOverdue || model.score <= 0.1,
                        }"
                        @click="() => viewModel.showFlagTip( model )" />
        </div>
        <div class="col-span-2 flex items-center justify-end">
            <CheckBox v-if="model.canBeDisabled"
                      v-model="model.isEnabled"
                      color="current"
                      class="flex-none"
                      @click.stop="() => viewModel.vehicle.condition.calculateScore()" /> 
            <CheckBox v-else
                      v-model="dummyEnabled"
                      color="current"
                      class="flex-none opacity-30"
                      @click.prevent="() => mandatoryDisabledClicked( model )" /> 
        </div>
    </div>

</template>